import { Form } from 'formik';
import { assign, isNull, keys, map, omitBy, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikCheckbox,
  FormikDialog,
  FormikSelect,
  FormikTextField,
  FormikUrlTextField,
} from 'components';
import { SciTemplate, SciTheme, SciView } from 'enums';
import { useMeta } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { SciDomainDto } from 'types';

import { CustomerBanksConfig } from './CustomerBanksConfig';

const STATIC_THEMES = [SciTheme.Light, SciTheme.SCI] as string[];

type Values = SciDomainDto;

type Props = DialogProps<CloseFormikDialogResult<Values>>;

export const SciDomainsDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
  ...rest
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.sci_domains',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const title = useMemo(
    () =>
      data ? t('details_dialog.edit_title') : t('details_dialog.create_title'),
    [t, data],
  );

  const {
    sciAvailableTemplateList: availableTemplates,
    sciAvailableThemeList: availableThemes,
  } = useMeta();

  const themesOptions = useMemo(
    () =>
      map(SciTheme, (theme) => ({
        value: theme,
        label: t(`themes.${theme}`, { defaultValue: theme }),
      })).filter(
        ({ value }) =>
          STATIC_THEMES.includes(value) || availableThemes.includes(value),
      ),
    [availableThemes, t],
  );

  const viewsOptions = useMemo(
    () =>
      map(SciView, (view) => ({
        value: view,
        label: t(`views.${view}`, { defaultValue: view }),
      })),
    [t],
  );

  const templatesOptions = useMemo(
    () =>
      map(SciTemplate, (template) => ({
        value: template,
        label: t(`templates.${template}`, { defaultValue: template }),
      })).filter(({ value }) =>
        [SciTemplate.Default, ...availableTemplates].includes(value),
      ),
    [t, availableTemplates],
  );

  const getInitialValues = useCallback(
    (): Values => ({
      url: '',
      logo: '',
      loader: '',
      title: '',
      copyright: '',
      theme: '',
      view: '',
      template: '',
      isDefault: false,
      hideShopName: false,
      telegramSupportBotUrl: '',
      customerBankConfigs: [],
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        url: Yup.string().required(tCommon('errors.required')),
        logo: Yup.string(),
        loader: Yup.string(),
        title: Yup.string(),
        copyright: Yup.string(),
        theme: Yup.string()
          .nullable()
          .oneOf([...STATIC_THEMES, ...availableThemes, ''] as (
            | SciTheme
            | ''
          )[]),
        view: Yup.string()
          .nullable()
          .oneOf([...Object.values(SciView), ''] as (SciView | '')[]),
        template: Yup.string()
          .nullable()
          .oneOf([SciTemplate.Default, ...availableTemplates, ''] as (
            | SciTemplate
            | ''
          )[]),
        isDefault: Yup.boolean(),
        hideShopName: Yup.boolean(),
        telegramSupportBotUrl: Yup.string(),
        customerBankConfigs: Yup.array().of(
          Yup.object().shape({
            bank: Yup.string().required(tCommon('errors.required')),
            customerBanks: Yup.array()
              .of(Yup.string().required(tCommon('errors.required')))
              .required(tCommon('errors.required')),
          }),
        ),
      }),
    [tCommon, availableTemplates, availableThemes],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(
      assign(_initialValues, omitBy(pick(data, keys(_initialValues)), isNull)),
    );
  }, [data, getInitialValues]);

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose,
        maxWidth: 'sm',
      }}
      formikProps={{
        initialValues,
        validateOnMount: true,
        validationSchema,
      }}
    >
      {() => (
        <Form>
          <FormControls>
            <FormikUrlTextField label={t('fields.url')} name="url" required />
            <FormikTextField label={t('fields.logo')} name="logo" />
            <FormikTextField label={t('fields.loader')} name="loader" />
            <FormikTextField label={t('fields.title')} name="title" />
            <FormikTextField label={t('fields.copyright')} name="copyright" />
            <FormikSelect
              label={t('fields.theme')}
              name="theme"
              options={themesOptions}
              noneOption
            />
            <FormikSelect
              label={t('fields.view')}
              name="view"
              options={viewsOptions}
              noneOption
            />
            {templatesOptions.length > 1 && (
              <FormikSelect
                label={t('fields.template')}
                name="template"
                options={templatesOptions}
                noneOption
              />
            )}
            <FormikTextField
              label={t('fields.telegramSupportBotUrl')}
              name="telegramSupportBotUrl"
            />
            <FormikCheckbox label={t('fields.is_default')} name="isDefault" />
            <FormikCheckbox
              label={t('fields.hide_shop_name')}
              name="hideShopName"
            />
            <CustomerBanksConfig />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
